<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import { useI18n } from '@/i18n';
import { fileToDataUrl } from '@/lib/fileToDataUrl';
import { Avatar, Badge, Button, LayoutGap, Modal, Prose } from '@dabble/quillui';
import { ref, useTemplateRef, watch } from 'vue';
import AvatarCropper from './AvatarCropper.vue';

const { t } = useI18n();
const { showToast } = useToast();

const open = defineModel<boolean>('open', { default: false });

const { applying = false } = defineProps<{
  applying?: boolean;
}>();

const emit = defineEmits<{
  apply: [blob: Blob];
}>();

const currentStep = ref<'upload' | 'crop'>('upload');
const fileInputRef = useTemplateRef<HTMLInputElement>('fileInput');
const isOverDropZone = ref(false);

const cropperRef = ref<InstanceType<typeof AvatarCropper> | null>(null);
const imageUrl = ref<string | null>(null);
const zoom = ref(0);
const previewUrl = ref<string | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const debounceUpdatePreview = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(updatePreview, 300);
};

const handleFileDrop = async (file: File) => {
  if (!file.type.startsWith('image/')) {
    showToast('warning', t('image_upload_must_be_image'));
    return;
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    imageUrl.value = dataUrl;
    currentStep.value = 'crop';
  } catch {
    showToast('danger', t('image_upload_read_failed'));
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  isOverDropZone.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isOverDropZone.value = false;
};

const handleDrop = async (event: DragEvent) => {
  event.preventDefault();
  isOverDropZone.value = false;

  const file = Array.from(event.dataTransfer?.files ?? []).find((f) => f.type.startsWith('image/'));
  if (file) {
    await handleFileDrop(file);
  }
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    await handleFileDrop(file);
  }
  target.value = '';
};

const handleUploadClick = () => {
  fileInputRef.value?.click();
};

const updatePreview = async () => {
  if (!cropperRef.value) return;
  const result = await cropperRef.value.getResult('blob', 'full');
  if (!cropperRef.value || !open.value) return;
  if (!(result instanceof Blob)) return;
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = URL.createObjectURL(result);
};

const handleCropUpdate = () => {
  debounceUpdatePreview();
};

watch(zoom, () => {
  debounceUpdatePreview();
});

const handleReset = () => {
  currentStep.value = 'upload';
  imageUrl.value = null;
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
  }
  previewUrl.value = null;
  zoom.value = 0;
};

const handleApply = async () => {
  if (!cropperRef.value) return;
  const blob = await cropperRef.value.getResult('blob', 'full');
  if (blob instanceof Blob) {
    emit('apply', blob);
  }
};

watch(open, (isOpen) => {
  if (!isOpen) {
    currentStep.value = 'upload';
    imageUrl.value = null;
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value);
    }
    previewUrl.value = null;
    zoom.value = 0;
  }
});
</script>

<template>
  <Modal v-model="open" size="fitContent">
    <template v-if="currentStep === 'upload'">
      <div class="avatar-cropper-modal__step">
        <div
          class="avatar-cropper-modal__dropzone"
          :class="{ 'avatar-cropper-modal__dropzone--is-dragging': isOverDropZone }"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
          @drop="handleDrop"
        >
          <LayoutGap
            direction="column"
            align="center"
            vertical-align="center"
            size="xxsmall"
            class="avatar-cropper-modal__upload-container"
          >
            <Prose variant="muted" size="medium" weight="semibold" font-style="uppercase" align="center">
              {{ t('image_upload_drag_and_drop') }}<br />{{ t('image_upload_drag_and_drop_instructions') }}
            </Prose>
            <span class="avatar-cropper-modal__hint">{{ t('avatar_image_upload_hint') }}</span>
            <LayoutGap align="center">
              <Button icon-left="image" variant="secondary" finish="outlined" @click="handleUploadClick">
                {{ t('upload') }}
              </Button>
            </LayoutGap>
            <input
              ref="fileInput"
              class="avatar-cropper-modal__file-input"
              type="file"
              accept="image/*"
              @change="handleFileSelect"
            />
          </LayoutGap>
        </div>
      </div>
    </template>

    <template v-else-if="currentStep === 'crop' && imageUrl">
      <div class="avatar-cropper-modal__step">
        <LayoutGap size="medium" align="center" vertical-align="stretch" inline>
          <AvatarCropper
            ref="cropperRef"
            v-model:zoom="zoom"
            :image-url="imageUrl"
            viewport-shape="circle"
            :viewport-width="220"
            :viewport-height="220"
            :boundary-width="280"
            :boundary-height="280"
            :output-width="256"
            :output-height="256"
            full-result-format="jpeg"
            :full-result-quality="0.9"
            :thumbnail-width="128"
            :thumbnail-height="128"
            @update="handleCropUpdate"
            @ready="updatePreview"
          />

          <LayoutGap
            class="avatar-cropper-modal__preview"
            direction="column"
            align="center"
            vertical-align="center"
            size="xxsmall"
          >
            <Avatar v-if="previewUrl" :url="previewUrl" size="xhuge" />
            <Badge variant="info" size="small">{{ t('avatar_image_preview') }}</Badge>
          </LayoutGap>
        </LayoutGap>

        <LayoutGap size="xxsmall" align="right" vertical-align="center" class="avatar-cropper-modal__actions">
          <Button variant="secondary" finish="outlined" :disabled="applying" @click="handleReset">
            {{ t('avatar_image_reset') }}
          </Button>
          <Button variant="primary" finish="filled" :disabled="applying" @click="handleApply">
            {{ applying ? t('avatar_image_applying') : t('avatar_image_apply') }}
          </Button>
        </LayoutGap>
      </div>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.avatar-cropper-modal {
  &__step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('medium');
  }

  &__dropzone {
    border: space('tiny') dashed theme('color-border-primary');
    border-radius: borderRadius('tiny');
    transition:
      border-color 0.25s ease-in-out,
      background-color 0.2s ease;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: space('large');
    text-align: center;
    width: 360px;
    min-height: 220px;

    &--is-dragging {
      background-color: theme('color-bg-info');
      border-color: theme('color-border-secondary');
    }
  }

  &__upload-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__hint {
    display: block;
    color: theme('color-text-placeholder');
    font-size: fontSize('xsmall');
    font-style: italic;
  }

  &__file-input {
    display: none;
  }

  &__preview {
    min-width: 140px;
  }

  &__actions {
    align-self: stretch;
  }
}
</style>
